import { createContext, useContext, type ReactElement, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@openuidev/react-ui/Accordion'
import { AreaChart, BarChart, HorizontalBarChart, LineChart, PieChart } from '@openuidev/react-ui/Charts'
import { MetricIndicatorInline } from '@openuidev/react-ui/MetricIndicator'
import { Steps, StepsItem } from '@openuidev/react-ui/Steps'
import { useTheme } from '@openuidev/react-ui/ThemeProvider'
import '@openuidev/react-ui/styles/accordion.css'
import '@openuidev/react-ui/styles/charts.css'
import '@openuidev/react-ui/styles/metricIndicator.css'
import '@openuidev/react-ui/styles/steps.css'
import { orderedPalette } from './openuiTheme'
import { createLibrary, defineComponent, useTriggerAction } from '@openuidev/react-lang'
import { z } from 'zod'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { rendersAsPrimary, toTone, usePrimaryActionKey, type Tone } from './miniAppRules'

// Aurora catalogue (os-aurora-migration § 4.2): flat cards on --card with a
// 1 px --border line and a 16 px radius, no shadow and no transparency; pills
// for actions, tabs and badges; content in Manrope (font-sans), the screen
// title alone in Space Grotesk (font-display) from 24 px.
const CARD = 'rounded-2xl border border-border bg-card'

// Depth of the container blocks: a CardBlock or SectionBlock directly under
// Root carries the screen title.
const DepthContext = createContext(0)

function LayoutBlock({
  layout,
  children,
}: {
  layout?: string
  children: ReactNode
}) {
  const className =
    layout === 'row'
      ? 'flex flex-wrap gap-4'
      : 'flex flex-col gap-4'

  return <div className={className}>{children}</div>
}

function BlockTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  const depth = useContext(DepthContext)
  const Heading = depth === 0 ? 'h2' : 'h3'
  return (
    <div className="min-w-0">
      <Heading
        className={
          depth === 0
            ? 'font-display text-2xl font-semibold leading-tight text-foreground'
            : 'text-base font-semibold text-foreground'
        }
      >
        {title}
      </Heading>
      {subtitle ? <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p> : null}
    </div>
  )
}

const TONE_DOT: Record<'success' | 'warning' | 'error', string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
}

const TONE_LABEL_KEY = {
  success: 'common.miniappToneSuccess',
  warning: 'common.miniappToneWarning',
  error: 'common.miniappToneError',
} as const

// A state is never shown by colour alone: 10 px dot + label.
function ToneStatus({ tone }: { tone: Tone }) {
  const { t } = useTranslation()
  if (tone !== 'success' && tone !== 'warning' && tone !== 'error') return null
  return (
    <span className="mt-2 inline-flex items-center gap-2 text-[13px] font-semibold text-foreground" data-tone={tone}>
      <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${TONE_DOT[tone]}`} />
      {t(TONE_LABEL_KEY[tone])}
    </span>
  )
}

const Root = defineComponent({
  name: 'Root',
  props: z.object({
    content: z.any(),
  }),
  description: 'Container racine pour une mini-app Agent Maurice.',
  component: ({ props, renderNode }) => (
    <DepthContext.Provider value={0}>
      <div className="flex flex-col gap-4 font-sans text-foreground">{renderNode(props.content)}</div>
    </DepthContext.Provider>
  ),
})

const TextBlock = defineComponent({
  name: 'TextBlock',
  props: z.object({
    value: z.string(),
  }),
  description: 'Bloc de texte simple.',
  component: ({ props }) => <p className="text-sm text-foreground">{props.value}</p>,
})

const StatCard = defineComponent({
  name: 'StatCard',
  props: z.object({
    label: z.string(),
    value: z.string(),
  }),
  description: 'Carte de statistique label/valeur.',
  component: ({ props }) => (
    <div className={`${CARD} px-4 py-3`}>
      <p className="text-[13px] font-semibold text-muted-foreground">{props.label}</p>
      <p className="mt-1 text-2xl font-semibold text-foreground">{props.value}</p>
    </div>
  ),
})

const KPIGridBlock = defineComponent({
  name: 'KPIGridBlock',
  props: z.object({
    items: z.array(
      z.object({
        label: z.string(),
        value: z.string(),
        tone: z.string().optional(),
      }),
    ),
  }),
  description: 'Grille compacte de KPI de synthèse.',
  component: ({ props }) => (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {props.items.map((item, index) => (
        <div key={`${item.label}-${index}`} className={`${CARD} px-4 py-3`}>
          <p className="text-[13px] font-semibold text-muted-foreground">{item.label}</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{item.value}</p>
          <ToneStatus tone={toTone(item.tone, 'neutral')} />
        </div>
      ))}
    </div>
  ),
})

const ActionButton = defineComponent({
  name: 'ActionButton',
  props: z.object({
    label: z.string(),
    actionId: z.string(),
  }),
  description: 'Bouton qui declenche un event de mini-app.',
  component: ({ props }) => {
    const triggerAction = useTriggerAction()

    return (
      <Button
        type="button"
        variant="secondary"
        onClick={() =>
          triggerAction(props.label, undefined, {
            type: 'miniapp_event',
            params: {
              actionId: props.actionId,
            },
          })}
      >
        {props.label}
      </Button>
    )
  },
})

const FormLinkButton = defineComponent({
  name: 'FormLinkButton',
  props: z.object({
    label: z.string(),
    formId: z.string(),
    submitEvent: z.string(),
  }),
  description: 'Bouton qui ouvre un formulaire de mini-app.',
  component: ({ props }) => {
    const triggerAction = useTriggerAction()

    return (
      <Button
        type="button"
        variant="secondary"
        onClick={() =>
          triggerAction(props.label, undefined, {
            type: 'form_link',
            params: {
              formId: props.formId,
              submitEvent: props.submitEvent,
            },
          })}
      >
        {props.label}
      </Button>
    )
  },
})

const ListBlock = defineComponent({
  name: 'ListBlock',
  props: z.object({
    items: z.array(z.string()),
    actionId: z.string(),
  }),
  description: 'Liste de valeurs textuelles optionnellement cliquables.',
  component: ({ props }) => {
    const triggerAction = useTriggerAction()
    const clickable = Boolean(props.actionId)

    return (
      <div className={`${CARD} overflow-hidden`}>
        {props.items.map((item, index) => (
          <button
            key={`${item}-${index}`}
            type="button"
            className={[
              'flex min-h-10 w-full items-center px-4 text-left text-sm text-foreground even:bg-card-raised',
              clickable ? 'hover:bg-primary-soft' : 'cursor-default',
            ].join(' ')}
            disabled={!clickable}
            onClick={() =>
              clickable
                ? triggerAction(item, undefined, {
                    type: 'miniapp_event',
                    params: {
                      actionId: props.actionId,
                      value: item,
                      index,
                    },
                  })
                : undefined
            }
          >
            {item}
          </button>
        ))}
      </div>
    )
  },
})

// Tabs and filters: pills with a line in a bar, the active one filled.
const PILL_BAR = 'flex flex-wrap gap-2'

const TabsBlock = defineComponent({
  name: 'TabsBlock',
  props: z.object({
    items: z.array(z.string()),
    active: z.string(),
    actionId: z.string(),
  }),
  description: 'Barre d onglets ou filtres de sélection rapide.',
  component: ({ props }) => {
    const triggerAction = useTriggerAction()
    return (
      <div className={PILL_BAR} role="group">
        {props.items.map((item, index) => {
          const active = props.active === item
          return (
            <Button
              key={`${item}-${index}`}
              type="button"
              size="sm"
              variant={active ? 'default' : 'secondary'}
              aria-pressed={active}
              onClick={() =>
                triggerAction(item, undefined, {
                  type: 'miniapp_event',
                  params: {
                    actionId: props.actionId,
                    value: item,
                    index,
                  },
                })}
            >
              {item}
            </Button>
          )
        })}
      </div>
    )
  },
})

const FilterBarBlock = defineComponent({
  name: 'FilterBarBlock',
  props: z.object({
    filters: z.array(
      z.object({
        label: z.string(),
        value: z.string(),
        actionId: z.string(),
        active: z.boolean().optional(),
      }),
    ),
  }),
  description: 'Barre de filtres actionnables pour affiner une vue.',
  component: ({ props }) => {
    const triggerAction = useTriggerAction()

    return (
      <div className={PILL_BAR} role="group">
        {props.filters.map((filter, index) => (
          <Button
            key={`${filter.label}-${filter.value}-${index}`}
            type="button"
            size="sm"
            variant={filter.active ? 'default' : 'secondary'}
            aria-pressed={Boolean(filter.active)}
            onClick={() =>
              triggerAction(`filter-${index}`, undefined, {
                type: 'miniapp_event',
                params: {
                  actionId: filter.actionId,
                  value: filter.value,
                  label: filter.label,
                },
              })
            }
          >
            <span>{filter.label}</span>
            <span className="font-medium">{filter.value}</span>
          </Button>
        ))}
      </div>
    )
  },
})

const ActionBarBlock = defineComponent({
  name: 'ActionBarBlock',
  props: z.object({
    actions: z.array(
      z.object({
        label: z.string(),
        variant: z.enum(['primary', 'secondary']).optional(),
        actionId: z.string().optional(),
        formId: z.string().optional(),
        submitEvent: z.string().optional(),
      }),
    ),
  }),
  description: 'Rangee compacte d actions contextuelles pour une selection ou une synthese.',
  component: ({ props }) => {
    const triggerAction = useTriggerAction()
    // One primary action per screen: only the action the renderer elected
    // keeps the accent; every other one is a secondary pill.
    const primaryKey = usePrimaryActionKey()

    return (
      <div className="flex flex-wrap gap-2">
        {props.actions.map((action, index) => {
          const clickableEvent = typeof action.actionId === 'string' && action.actionId.trim().length > 0
          const clickableForm = typeof action.formId === 'string' && action.formId.trim().length > 0
          if (!clickableEvent && !clickableForm) {
            return null
          }
          const primary = rendersAsPrimary(action, primaryKey)

          return (
            <Button
              key={`${action.label}-${index}`}
              type="button"
              variant={primary ? 'default' : 'secondary'}
              data-primary={primary ? 'true' : undefined}
              onClick={() =>
                clickableForm
                  ? triggerAction(action.label, undefined, {
                      type: 'form_link',
                      params: {
                        formId: action.formId,
                        submitEvent: action.submitEvent ?? '',
                      },
                    })
                  : triggerAction(action.label, undefined, {
                      type: 'miniapp_event',
                      params: {
                        actionId: action.actionId,
                      },
                    })}
            >
              {action.label}
            </Button>
          )
        })}
      </div>
    )
  },
})

const BADGE_VARIANT: Record<Tone, 'success' | 'warning' | 'destructive' | 'info' | 'default'> = {
  success: 'success',
  warning: 'warning',
  error: 'destructive',
  info: 'info',
  neutral: 'default',
}

const BadgeRowBlock = defineComponent({
  name: 'BadgeRowBlock',
  props: z.object({
    items: z.array(
      z.object({
        label: z.string(),
        value: z.string(),
        tone: z.string().optional(),
      }),
    ),
  }),
  description: 'Rangee compacte de badges pour statuts et metadonnees courtes.',
  component: ({ props }) => (
    <div className="flex flex-wrap gap-2">
      {props.items.map((item, index) => {
        const tone = toTone(item.tone, 'neutral')
        return (
          <Badge
            key={`${item.label}-${index}`}
            variant={BADGE_VARIANT[tone]}
            className="gap-2 px-3 py-1"
            data-tone={tone}
          >
            <span className="font-normal">{item.label}</span>
            <span>{item.value}</span>
          </Badge>
        )
      })}
    </div>
  ),
})

function ContainerBlock({
  title,
  subtitle,
  layout,
  children,
  renderNode,
}: {
  title: string
  subtitle: string
  layout: string
  children: unknown[]
  renderNode: (node: unknown) => ReactNode
}) {
  const depth = useContext(DepthContext)
  return (
    <div className={`${CARD} space-y-4 p-4`}>
      {title ? <BlockTitle title={title} subtitle={subtitle} /> : null}
      <DepthContext.Provider value={depth + 1}>
        <LayoutBlock layout={layout}>
          {children.map((child, index) => (
            <div key={index} className="min-w-0 flex-1">
              {renderNode(child)}
            </div>
          ))}
        </LayoutBlock>
      </DepthContext.Provider>
    </div>
  )
}

const SectionBlock = defineComponent({
  name: 'SectionBlock',
  props: z.object({
    title: z.string(),
    subtitle: z.string(),
    layout: z.string(),
    children: z.array(z.any()),
  }),
  description: 'Section de composants avec titre optionnel.',
  component: ({ props, renderNode }) => (
    <ContainerBlock
      title={props.title}
      subtitle={props.subtitle}
      layout={props.layout}
      renderNode={renderNode}
    >
      {props.children}
    </ContainerBlock>
  ),
})

const CALLOUT: Record<Tone, { className: string; Icon: typeof Info }> = {
  success: { className: 'border-success bg-success-soft text-success', Icon: CheckCircle2 },
  warning: { className: 'border-warning bg-warning-soft text-warning', Icon: AlertTriangle },
  error: { className: 'border-error bg-error-soft text-error', Icon: XCircle },
  info: { className: 'border-info bg-info-soft text-info', Icon: Info },
  neutral: { className: 'border-border bg-card-raised text-foreground', Icon: Info },
}

const CalloutBlock = defineComponent({
  name: 'CalloutBlock',
  props: z.object({
    title: z.string(),
    value: z.string(),
    tone: z.string(),
  }),
  description: 'Encart de mise en avant avec un ton visuel.',
  component: ({ props }) => {
    const tone = toTone(props.tone, 'info')
    const { className, Icon } = CALLOUT[tone]

    return (
      <div className={`flex gap-3 rounded-2xl border px-4 py-3 ${className}`} data-tone={tone}>
        <Icon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0">
          {props.title ? <p className="text-sm font-semibold">{props.title}</p> : null}
          <p className={props.title ? 'mt-1 text-sm' : 'text-sm'}>{props.value}</p>
        </div>
      </div>
    )
  },
})

const EmptyStateBlock = defineComponent({
  name: 'EmptyStateBlock',
  props: z.object({
    title: z.string(),
    description: z.string(),
    actionLabel: z.string(),
    actionId: z.string(),
  }),
  description: 'Etat vide avec texte d explication et action optionnelle.',
  component: ({ props }) => {
    const triggerAction = useTriggerAction()
    const hasAction = props.actionLabel.trim().length > 0 && props.actionId.trim().length > 0

    return (
      <div className={`${CARD} px-6 py-8 text-center`}>
        <p className="font-display text-xl font-semibold text-foreground">{props.title}</p>
        <p className="mt-2 text-sm text-muted-foreground">{props.description}</p>
        {hasAction ? (
          <Button
            type="button"
            variant="secondary"
            className="mt-4"
            onClick={() =>
              triggerAction(props.actionLabel, undefined, {
                type: 'miniapp_event',
                params: {
                  actionId: props.actionId,
                },
              })}
          >
            {props.actionLabel}
          </Button>
        ) : null}
      </div>
    )
  },
})

// chartRows keeps only the category and the plotted value: the OpenUI charts
// draw every other numeric column as a series.
export function chartRows(
  data: Array<Record<string, string | number | boolean | null>>,
  xKey: string,
  yKey: string,
): Array<Record<string, string | number>> {
  return data.flatMap((row) => {
    const value = Number(row?.[yKey])
    if (!row || !Number.isFinite(value)) return []
    return [{ [xKey]: String(row[xKey] ?? ''), [yKey]: value }]
  })
}

// pieRows keeps one slice per palette slot: beyond it, the smallest slices
// fold into a single "Other" slice rather than reusing a colour.
export function pieRows(
  rows: Array<Record<string, string | number>>,
  xKey: string,
  yKey: string,
  slots: number,
  otherLabel: string,
): Array<Record<string, string | number>> {
  if (rows.length <= slots) return rows
  const sorted = [...rows].sort((a, b) => Number(b[yKey]) - Number(a[yKey]))
  const kept = sorted.slice(0, slots - 1)
  const other = sorted.slice(slots - 1).reduce((sum, row) => sum + Number(row[yKey]), 0)
  return [...kept, { [xKey]: otherLabel, [yKey]: other }]
}

function ChartView({
  title,
  chartType,
  xKey,
  yKey,
  data,
}: {
  title: string
  chartType: 'bar' | 'line' | 'area' | 'pie' | 'horizontal_bar'
  xKey: string
  yKey: string
  data: Array<Record<string, string | number | boolean | null>>
}) {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const palette = theme.defaultChartPalette ?? []
  let rows = chartRows(data, xKey, yKey)
  if (chartType === 'pie' && palette.length > 0) rows = pieRows(rows, xKey, yKey, palette.length, t('common.miniappOther'))
  // One plotted series, or one colour per pie slice, in palette order.
  const customPalette = palette.length > 0 ? orderedPalette(palette, chartType === 'pie' ? rows.length : 1) : undefined
  const common = { data: rows, categoryKey: xKey, isAnimationActive: false, legend: false, customPalette }
  let chart: ReactElement
  switch (chartType) {
    case 'line':
      chart = <LineChart {...common} />
      break
    case 'area':
      chart = <AreaChart {...common} />
      break
    case 'pie':
      chart = <PieChart data={rows} categoryKey={xKey} dataKey={yKey} isAnimationActive={false} customPalette={customPalette} />
      break
    case 'horizontal_bar':
      chart = <HorizontalBarChart {...common} />
      break
    default:
      chart = <BarChart {...common} />
  }
  return (
    <figure className={`${CARD} p-4`} data-chart-type={chartType}>
      {title ? <figcaption className="mb-3 text-sm font-semibold text-foreground">{title}</figcaption> : null}
      {rows.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground">{t('common.miniappNoData')}</p>
      ) : (
        chart
      )}
    </figure>
  )
}

const ChartBlock = defineComponent({
  name: 'ChartBlock',
  props: z.object({
    title: z.string(),
    chartType: z.enum(['bar', 'line', 'area', 'pie', 'horizontal_bar']),
    axes: z.object({
      xKey: z.string(),
      yKey: z.string(),
    }),
    data: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))),
  }),
  description: 'Graphique OpenUI : bar, line, area, pie ou horizontal_bar.',
  component: ({ props }) => (
    <ChartView
      title={props.title}
      chartType={props.chartType}
      xKey={props.axes.xKey}
      yKey={props.axes.yKey}
      data={props.data}
    />
  ),
})

const DataTableBlock = defineComponent({
  name: 'DataTableBlock',
  props: z.object({
    columns: z.array(z.string()),
    rows: z.array(
      z.union([
        z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])),
        z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
      ]),
    ),
    actionId: z.string(),
    columnLabels: z.record(z.string(), z.string()).optional(),
  }),
  description: 'Tableau de données simple avec colonnes et lignes.',
  component: ({ props }) => {
    const triggerAction = useTriggerAction()
    const clickable = Boolean(props.actionId)

    return (
      <div className={`${CARD} overflow-x-auto`}>
        <table className="min-w-full text-sm">
          <thead className="border-b border-border">
            <tr>
              {props.columns.map((column) => (
                <th
                  key={column}
                  className="h-10 px-3 text-left text-[13px] font-semibold text-foreground"
                >
                  {props.columnLabels?.[column] ?? column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {props.rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={clickable ? 'h-10 cursor-pointer even:bg-card-raised hover:bg-primary-soft' : 'h-10 even:bg-card-raised'}
                onClick={() =>
                  clickable
                    ? triggerAction(`row-${rowIndex}`, undefined, {
                        type: 'miniapp_event',
                        params: {
                          actionId: props.actionId,
                          value: row,
                          index: rowIndex,
                        },
                      })
                    : undefined
                }
              >
                {(Array.isArray(row)
                  ? row
                  : props.columns.map((column) => row[column] ?? null)
                ).map((cell, cellIndex) => (
                  <td key={`${rowIndex}-${cellIndex}`} className="px-3 text-foreground">
                    {cell == null ? '—' : String(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  },
})

const CardBlock = defineComponent({
  name: 'CardBlock',
  props: z.object({
    title: z.string(),
    subtitle: z.string(),
    layout: z.string(),
    children: z.array(z.any()),
  }),
  description: 'Carte contenant une collection de composants.',
  component: ({ props, renderNode }) => <CardBlockView {...props} renderNode={renderNode} />,
})

function CardBlockView({
  title,
  subtitle,
  layout,
  children,
  renderNode,
}: {
  title: string
  subtitle: string
  layout: string
  children: unknown[]
  renderNode: (node: unknown) => ReactNode
}) {
  const depth = useContext(DepthContext)
  return (
    <Card>
      {title ? (
        <CardHeader className="p-4 pb-3">
          <BlockTitle title={title} subtitle={subtitle} />
        </CardHeader>
      ) : null}
      <CardContent className={title ? 'p-4 pt-0' : 'p-4'}>
        <DepthContext.Provider value={depth + 1}>
          <LayoutBlock layout={layout}>
            {children.map((child, index) => (
              <div key={index} className="min-w-0 flex-1">
                {renderNode(child)}
              </div>
            ))}
          </LayoutBlock>
        </DepthContext.Provider>
      </CardContent>
    </Card>
  )
}

const SeparatorBlock = defineComponent({
  name: 'SeparatorBlock',
  props: z.object({}),
  description: 'Séparateur horizontal entre sections.',
  component: () => <div className="border-t border-border my-4" />,
})

const AccordionBlock = defineComponent({
  name: 'AccordionBlock',
  props: z.object({
    titles: z.array(z.string()),
    panels: z.array(z.any()),
  }),
  description: 'Accordéon OpenUI ; le premier panneau est ouvert.',
  component: ({ props, renderNode }) => (
    <Accordion type="multiple" defaultValue={props.titles.length > 0 ? ['panel-0'] : []}>
      {props.titles.map((title, index) => (
        <AccordionItem key={index} value={`panel-${index}`}>
          <AccordionTrigger text={title} />
          <AccordionContent>{props.panels[index] ? renderNode(props.panels[index]) : null}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  ),
})

const trendArrow = { up: '↑', down: '↓', neutral: '' } as const

const MetricBlock = defineComponent({
  name: 'MetricBlock',
  props: z.object({
    label: z.string(),
    value: z.string(),
    delta: z.string(),
    trend: z.enum(['up', 'down', 'neutral']),
    caption: z.string(),
  }),
  description: 'Métrique OpenUI ; un delta en pourcentage pilote l’indicateur de tendance.',
  component: ({ props }) => {
    // The OpenUI trend indicator shows a percentage: only a delta written as
    // one drives it; any other delta stays visible as written.
    const text = props.delta.trim()
    const percent = /%$/.test(text) ? Number.parseFloat(text.replace(',', '.')) : Number.NaN
    const indicator =
      props.trend !== 'neutral' && Number.isFinite(percent)
        ? { direction: props.trend, value: Math.abs(percent) }
        : undefined
    const shownDelta = indicator || !text ? '' : `${trendArrow[props.trend]} ${text}`.trim()
    const subtext = [shownDelta, props.caption].filter(Boolean).join(' · ')
    return (
      <div className={`${CARD} p-4`} data-trend={props.trend}>
        <p className="text-[13px] font-semibold text-muted-foreground">{props.label}</p>
        <MetricIndicatorInline value={props.value} subtext={subtext || undefined} trend={indicator} />
      </div>
    )
  },
})

const stepStatusKey = {
  done: 'common.miniappStepDone',
  current: 'common.miniappStepCurrent',
  todo: 'common.miniappStepTodo',
} as const

function StepsView({ items }: { items: Array<{ title: string; description?: string; status: keyof typeof stepStatusKey }> }) {
  const { t } = useTranslation()
  return (
    <Steps>
      {items.map((item, index) => (
        <StepsItem
          key={index}
          number={index + 1}
          title={
            <span className="inline-flex items-baseline gap-2" data-status={item.status}>
              {item.title}
              <span
                className={
                  item.status === 'done'
                    ? 'text-xs text-success'
                    : item.status === 'current'
                      ? 'text-xs font-semibold text-link'
                      : 'text-xs text-muted-foreground'
                }
              >
                {t(stepStatusKey[item.status])}
              </span>
            </span>
          }
          details={item.description ?? ''}
        />
      ))}
    </Steps>
  )
}

const StepsBlock = defineComponent({
  name: 'StepsBlock',
  props: z.object({
    items: z.array(
      z.object({
        title: z.string(),
        description: z.string().optional(),
        status: z.enum(['done', 'current', 'todo']),
      }),
    ),
  }),
  description: 'Étapes OpenUI avec statut.',
  component: ({ props }) => <StepsView items={props.items} />,
})

export const miniAppOpenUILibrary = createLibrary({
  components: [
    Root,
    TextBlock,
    StatCard,
    KPIGridBlock,
    ActionButton,
    FormLinkButton,
    ListBlock,
    TabsBlock,
    FilterBarBlock,
    ActionBarBlock,
    BadgeRowBlock,
    CalloutBlock,
    EmptyStateBlock,
    ChartBlock,
    DataTableBlock,
    SectionBlock,
    CardBlock,
    SeparatorBlock,
    AccordionBlock,
    MetricBlock,
    StepsBlock,
  ],
  root: 'Root',
})
